import { HttpClient } from 'http-client/apiClient';
import { ApiResponse } from 'http-client/types/auth.types';
import { PaginatedResponse, TableQueryParams } from 'types/table.types';

/**
 * Generic data table API service
 */
export class DataTableApiService {
  /**
   * Fetch paginated data with filtering, sorting, and searching
   */
  static async fetchPaginatedData<T>(
    endpoint: string,
    params: TableQueryParams,
  ): Promise<PaginatedResponse<T>> {
    try {
      const response = await HttpClient.get<ApiResponse<PaginatedResponse<T>>>(endpoint, {
        params: {
          pageNumber: params.pageNumber ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.search && { search: params.search }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortDirection && { sortDirection: params.sortDirection }),
          // Pass through any other filter parameters
          ...Object.fromEntries(
            Object.entries(params).filter(
              ([key]) =>
                !['pageNumber', 'pageSize', 'search', 'sortBy', 'sortDirection'].includes(key),
            ),
          ),
        },
      });

      if (!response?.success) {
        console.error('API Error:', response?.message || response?.errors);
        throw new Error(response?.message || 'Failed to fetch data');
      }

      return (
        response.data ?? {
          items: [],
          pageNumber: 1,
          pageSize: 20,
          totalCount: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        }
      );
    } catch (error) {
      console.error('Failed to fetch paginated data:', error);
      throw error;
    }
  }

  /**
   * Fetch all items without pagination (use carefully with large datasets)
   */
  static async fetchAllData<T>(endpoint: string, params: Record<string, any> = {}): Promise<T[]> {
    try {
      const response = await HttpClient.get<ApiResponse<T[]>>(endpoint, { params });

      if (!response?.success) {
        console.error('API Error:', response?.message || response?.errors);
        throw new Error(response?.message || 'Failed to fetch data');
      }

      return response.data ?? [];
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  }

  /**
   * Fetch single item by ID
   */
  static async fetchItemById<T>(endpoint: string, id: string | number): Promise<T | null> {
    try {
      const response = await HttpClient.get<ApiResponse<T>>(`${endpoint}/${id}`);

      if (!response?.success) {
        console.error('API Error:', response?.message || response?.errors);
        throw new Error(response?.message || 'Failed to fetch item');
      }

      return response.data ?? null;
    } catch (error) {
      console.error('Failed to fetch item:', error);
      throw error;
    }
  }

  /**
   * Export data based on current filters/params
   * Note: This method downloads data as CSV/Excel from the server
   */
  static async exportData(
    endpoint: string,
    params: TableQueryParams,
    format: 'csv' | 'xlsx' = 'csv',
  ): Promise<void> {
    try {
      // Build query string
      const queryString = new URLSearchParams({
        ...params,
        format,
      } as any).toString();

      // Create download link
      const url = `${endpoint}/export?${queryString}`;
      const link = document.createElement('a');
      link.href = url;
      link.download = `export-${new Date().toISOString().split('T')[0]}.${
        format === 'xlsx' ? 'xlsx' : 'csv'
      }`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to export data:', error);
      throw error;
    }
  }
}
